import { RoutesProvider } from '@folklore/routes';
import { ConsentProvider } from '@micromag/core/contexts';
import { IntlProvider } from '@micromag/intl';
import React, { useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Router, type AroundNavHandler } from 'wouter';

import { prefersReducedMotion } from '../hooks/usePrefersReducedMotion';

import { AnalyticsProvider } from '../contexts/AnalyticsContext';
import { ModalProvider } from '../contexts/ModalContext';
import { SiteProvider } from '../contexts/SiteContext';
import defaultMicromags from '../micromags';
import { MicromagItem } from '../types/micromag';
import Routes from './Routes';

import '../styles/styles.css';

interface IntlProps {
    locale?: string;
    messages?: Record<string, Record<string, string>> | Record<string, string>;
}

interface AppProps {
    intl?: IntlProps | null;
    routes?: Record<string, string>;
    googleAnalyticsIds?: Array<string>;
    micromags?: MicromagItem[] | null;
}

function App({
    intl = null,
    routes = {
        home: '/',
        legal: '/mentions-legales',
        accessibility: '/accessibilite',
    },
    micromags = defaultMicromags,
    googleAnalyticsIds,
}: AppProps) {
    const { locale = 'fr', messages = null } = intl || {};
    const finalMessages = messages !== null && messages[locale] ? messages[locale] : messages;

    const aroundNav: AroundNavHandler = (navigate, to, options) => {
        if (!document.startViewTransition || prefersReducedMotion()) {
            navigate(to, options);
            window.scrollTo(0, 0);
            return;
        }
        document.startViewTransition(() => {
            flushSync(() => {
                navigate(to, options);
                window.scrollTo(0, 0);
            });
        });
    };

    return (
        <IntlProvider locale={locale} messages={finalMessages || {}}>
            <ConsentProvider consent={['functionality_storage', 'analytics_storage']}>
                <ModalProvider>
                    <SiteProvider>
                        <AnalyticsProvider googleAnalyticsIds={googleAnalyticsIds || null}>
                            <Router aroundNav={aroundNav}>
                                <RoutesProvider routes={routes}>
                                    <Routes micromags={micromags} />
                                </RoutesProvider>
                            </Router>
                        </AnalyticsProvider>
                    </SiteProvider>
                </ModalProvider>
            </ConsentProvider>
        </IntlProvider>
    );
}

export default App;
